import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Loader,
  NotificationModalType,
  analyticsClient,
} from '@spotted-zebra-uk/ui-components';
import { ModalTypes } from '../../enums/modalTypes';
import { closeModal, openModal } from '../../graphql/modals';
import { getRoleHomeUrl } from '../../constants/routes';
import {
  AiInterviewAssessmentStageActionCandidateStatus,
  Avatar,
  GrammaticalGender,
  SupportedLocale,
  useAiInterviewRoleQuery,
  useGetConferenceQuery,
  useInitiateAiInterviewMutation,
} from '../../generated/graphql';
import { ConferenceLobby } from './ConferenceLobby/ConferenceLobby';
import { useAiInterviewIntegrityMonitoring } from './hooks/useAiInterviewIntegrityMonitoring';
import { useAiInterviewVisionIntegrityDetection } from './hooks/useAiInterviewVisionIntegrityDetection';
import { useConference } from './hooks/useConference';
import { useDemoConference } from './hooks/useDemoConference';
import { useDemoTimer } from './hooks/useDemoTimer';
import { useShowInterviewMonitoringUi } from './hooks/useShowInterviewMonitoringUi';
import { Layout } from './Layout/Layout';
import { ThankYou } from './ThankYou/ThankYou';
import { AvatarOption } from './AvatarSelection/avatarOptions';
import { ConfigWizard } from './ConfigWizard/ConfigWizard';
import { ConsentGate } from './ConsentGate/ConsentGate';
import { isAiInterviewConsentPending } from './ConsentGate/consentGate.helpers';
import { PracticeCompletion } from './ConfigWizard/PracticeCompletion';
import { DemoTestCallBanner } from './demo/DemoTestCallBanner';
import { HEAP_ANALYTICS_EVENTS } from '@/constants/analytics';
import { useSupportEmail } from '@/contexts/SupportEmail/SupportEmailContext';

let isBeforeUnloadActive = false;

export function getIsBeforeUnloadActive() {
  return isBeforeUnloadActive;
}

const POLLING_INTERVAL = 3_000;
const MAX_POLLING_DURATION = 60_000;
const DEMO_DURATION_SECONDS = 180;

type Phase =
  | { type: 'wizard'; initialStepIndex?: number }
  | { type: 'practice' }
  | { type: 'practice-complete' }
  | { type: 'interview' }
  | { type: 'thank-you' };

export function ConferencePage() {
  const [phase, setPhase] = useState<Phase>({ type: 'wizard' });
  const [pollingInterval, setPollingInterval] = useState(0);
  const [avatarEnabled, setAvatarEnabled] = useState<boolean | null>(null);
  const [selectedAvatar, setSelectedAvatar] = useState<AvatarOption | null>(null);
  const [selectedLocale, setSelectedLocale] = useState<SupportedLocale>();
  const [selectedGrammaticalGender, setSelectedGrammaticalGender] =
    useState<GrammaticalGender>();

  const { t } = useTranslation();
  const { supportEmail } = useSupportEmail();
  const navigate = useNavigate();
  const params = useParams();
  const showMonitoringUi = useShowInterviewMonitoringUi();

  const roleId = params.roleId ?? '';
  const aiInterviewCandidateId =
    params.aiInterviewAssessmentStageActionCandidateId ?? '';

  const { data: roleData, loading: roleLoading } = useAiInterviewRoleQuery({
    variables: { id: roleId },
    skip: !roleId,
  });

  const { data: conferenceData, refetch: refetchConference } =
    useGetConferenceQuery({
      variables: { id: aiInterviewCandidateId },
      pollInterval: pollingInterval,
      skip: !aiInterviewCandidateId,
    });

  const conferenceId =
    conferenceData?.aiInterviewAssessmentStageActionCandidate?.conference?.id;

  const actionCandidate =
    conferenceData?.aiInterviewAssessmentStageActionCandidate;

  // Backend-derived so it matches what the interviewer agent will say out
  // loud: the guide's own budgets, stretched by any adjustment, rounded up to
  // the next 5 minutes. 0 means there is nothing to base a figure on.
  const estimatedDurationMinutes = actionCandidate?.estimatedDurationMinutes ?? 0;
  const timeModifierPercentage = actionCandidate?.timeModifierPercentage ?? 0;

  // Candidates who declared a difficulty with hearing or speaking are offered
  // an individual arrangement, on top of the extra time they already have.
  const shouldContactUs =
    conferenceData?.aiInterviewAssessmentStageActionCandidate?.stageCandidate
      ?.shouldContactUs ?? false;

  const conferenceStatus =
    conferenceData?.aiInterviewAssessmentStageActionCandidate?.status;

  // Consent gate (NIQ): the company and role require consent, and the
  // candidate has not given it for the current text.
  const consent = actionCandidate?.consent;
  const isConsentPending = isAiInterviewConsentPending(consent);
  const companyName = roleData?.myRole?.company?.name ?? '';

  useEffect(() => {
    if (avatarEnabled === null && conferenceData) {
      setAvatarEnabled(
        conferenceData.aiInterviewAssessmentStageActionCandidate?.stageAction
          ?.isAvatarEnabled ?? false
      );
    }
  }, [conferenceData, avatarEnabled]);

  const companyLogoUrl = roleData?.myRole?.company?.logoUrl ?? undefined;

  // Where leaving the interview lands: the role's portal when it has one,
  // its stage list otherwise.
  const exitUrl = getRoleHomeUrl(
    roleId,
    roleData?.myRole?.candidatePortalEnabled
  );

  const [initiateAiInterview] = useInitiateAiInterviewMutation({
    variables: { id: aiInterviewCandidateId },
  });

  // Real interview connection
  const { room, isConnected, error, joinConference, leaveConference } =
    useConference({
      conferenceId,
      aiInterviewCandidateId,
      onError: error => {
        console.error('Conference error:', error);
      },
    });

  const { openEvent, closeEvent, reportEvent } =
    useAiInterviewIntegrityMonitoring({
      room,
      aiInterviewCandidateId,
    });
  // Background camera analysis (MediaPipe): no-face / multiple-faces / phone
  // signals feed the same integrity session; invisible to the candidate.
  useAiInterviewVisionIntegrityDetection({
    room,
    enabled: isConnected,
    openEvent,
    closeEvent,
    reportEvent,
  });

  // Demo/practice connection
  const {
    room: demoRoom,
    isConnected: isDemoConnected,
    error: demoError,
    remainingDemos,
    joinDemoConference,
    leaveDemoConference,
  } = useDemoConference({
    conferenceId,
    onError: error => {
      console.error('Demo conference error:', error);
      openModal({
        modalType: ModalTypes.NOTIFICATION_MODAL,
        modalProps: {
          type: NotificationModalType.WARNING,
          title: t('aiInterview.demoConfig.somethingWentWrong'),
          message: t('aiInterview.demoConfig.startError'),
          isOpen: true,
          onClose: () => closeModal(ModalTypes.NOTIFICATION_MODAL),
          onOkClick: () => closeModal(ModalTypes.NOTIFICATION_MODAL),
        },
      });
      setPhase({ type: 'wizard', initialStepIndex: undefined });
    },
    onDemoLimitReached: () => {
      analyticsClient.trackEvent({
        name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_DEMO_LIMIT_REACHED,
      });
      openModal({
        modalType: ModalTypes.NOTIFICATION_MODAL,
        modalProps: {
          type: NotificationModalType.WARNING,
          title: t('aiInterview.demoConfig.limitReachedTitle'),
          message: t('aiInterview.demoConfig.limitReachedMessage'),
          isOpen: true,
          onClose: () => closeModal(ModalTypes.NOTIFICATION_MODAL),
          onOkClick: () => closeModal(ModalTypes.NOTIFICATION_MODAL),
        },
      });
    },
    onDisconnected: () => {
      analyticsClient.trackEvent({
        name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_DEMO_TEST_CALL_END_BY_TIMER,
      });
      setPhase({ type: 'practice-complete' });
    },
  });

  const { formattedTime } = useDemoTimer({
    durationSeconds: DEMO_DURATION_SECONDS,
    isRunning: isDemoConnected,
    onExpired: () => {
      // Server will disconnect shortly after token expiry
    },
  });

  const effectiveRemainingDemos =
    remainingDemos ??
    conferenceData?.aiInterviewAssessmentStageActionCandidate?.conference
      ?.remainingDemoCount ??
    null;
  const selectedLocales =
    conferenceData?.aiInterviewAssessmentStageActionCandidate?.stageAction
      ?.selectedLocales ?? [];

  // Blur warning during active interview
  useEffect(() => {
    if (!showMonitoringUi) return;
    if (!room || room.state !== 'connected') return;

    const handleBlur = () => {
      openModal({
        modalType: ModalTypes.NOTIFICATION_MODAL,
        modalProps: {
          type: NotificationModalType.WARNING,
          title: t('aiInterview.leftWindow.title'),
          message: t('aiInterview.leftWindow.message'),
          isOpen: true,
          hasCancelButton: false,
          okButtonChildren: t('common.ok'),
          onClose: () => closeModal(ModalTypes.NOTIFICATION_MODAL),
          onOkClick: () => closeModal(ModalTypes.NOTIFICATION_MODAL),
        },
      });
    };

    window.addEventListener('blur', handleBlur);
    return () => window.removeEventListener('blur', handleBlur);
  }, [room?.state, showMonitoringUi]);

  // Beforeunload protection during active interview
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (room && room.state === 'connected') {
        isBeforeUnloadActive = true;
        e.preventDefault();
        setTimeout(() => {
          isBeforeUnloadActive = false;
        }, 100);
      }
    };

    const handlePageHide = () => {
      if (room && room.state !== 'disconnected') {
        room.localParticipant.trackPublications.forEach(publication => {
          if (publication.track) {
            publication.track.stop();
            if (publication.track.mediaStreamTrack) {
              publication.track.mediaStreamTrack.stop();
            }
          }
        });
        room.disconnect();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [room]);

  // Beforeunload protection during practice
  useEffect(() => {
    if (phase.type !== 'practice') return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (demoRoom && demoRoom.state === 'connected') {
        e.preventDefault();
      }
    };

    const handlePageHide = () => {
      if (demoRoom && demoRoom.state !== 'disconnected') {
        leaveDemoConference();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [phase.type, demoRoom, leaveDemoConference]);

  // Conference status polling management
  useEffect(() => {
    // No initiation (and no conference) until consent is given: a decline
    // must not leave an orphan conference behind.
    if (isConsentPending) return;
    switch (conferenceStatus) {
      case AiInterviewAssessmentStageActionCandidateStatus.Ready:
      case AiInterviewAssessmentStageActionCandidateStatus.InProgress:
        setPollingInterval(0);
        break;
      case AiInterviewAssessmentStageActionCandidateStatus.NotStarted:
        initiateAiInterview();
        setPollingInterval(POLLING_INTERVAL);
        break;
      case AiInterviewAssessmentStageActionCandidateStatus.Initiated:
        setPollingInterval(POLLING_INTERVAL);
        break;
      case AiInterviewAssessmentStageActionCandidateStatus.Failed:
        setPollingInterval(0);
        openModal({
          modalType: ModalTypes.NOTIFICATION_MODAL,
          modalProps: {
            type: NotificationModalType.WARNING,
            title: t('aiInterview.demoConfig.somethingWentWrong'),
            message: t('aiInterview.interviewError', { email: supportEmail }),
            isOpen: true,
            onClose: () => {
              analyticsClient.trackEvent({
                name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_SOMETHING_WENT_WRONG_MODAL_DISMISS,
              });
              closeModal(ModalTypes.NOTIFICATION_MODAL);
              navigate(exitUrl);
            },
            onOkClick: () => {
              analyticsClient.trackEvent({
                name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_SOMETHING_WENT_WRONG_MODAL_DISMISS,
              });
              closeModal(ModalTypes.NOTIFICATION_MODAL);
              navigate(exitUrl);
            },
          },
        });
        break;
      case AiInterviewAssessmentStageActionCandidateStatus.Completed:
        setPollingInterval(0);
        openModal({
          modalType: ModalTypes.NOTIFICATION_MODAL,
          modalProps: {
            type: NotificationModalType.WARNING,
            title: t('aiInterview.alreadyCompleted.title'),
            message: t('aiInterview.alreadyCompleted.message'),
            isOpen: true,
            onClose: () => {
              closeModal(ModalTypes.NOTIFICATION_MODAL);
              navigate(exitUrl);
            },
            onOkClick: () => {
              closeModal(ModalTypes.NOTIFICATION_MODAL);
              navigate(exitUrl);
            },
          },
        });
        break;
      default:
        setPollingInterval(0);
        break;
    }

    return () => {
      setPollingInterval(0);
    };
  }, [conferenceStatus, isConsentPending]);

  // Safety cap: if the status gets stuck (e.g. a lost participant-joined event
  // leaving it in Ready), polling must not run until the tab closes
  useEffect(() => {
    if (!pollingInterval) return;

    const timeoutId = setTimeout(
      () => setPollingInterval(0),
      MAX_POLLING_DURATION
    );
    return () => clearTimeout(timeoutId);
  }, [pollingInterval]);

  // Phase transition handlers
  const handleStartPractice = useCallback(
    async (
      avatar: AvatarOption,
      locale: SupportedLocale,
      grammaticalGender?: GrammaticalGender
    ) => {
      setSelectedAvatar(avatar);
      setSelectedLocale(locale);
      setSelectedGrammaticalGender(grammaticalGender);
      setPhase({ type: 'practice' });

      analyticsClient.trackEvent({
        name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_DEMO_TEST_CALL_START,
      });

      await joinDemoConference({
        avatar: avatar.id,
        allowAgentInterruptions: true,
        locale,
        grammaticalGender,
      });
    },
    [joinDemoConference]
  );

  const handleStartInterview = useCallback(
    (
      avatar: AvatarOption,
      locale: SupportedLocale,
      grammaticalGender?: GrammaticalGender
    ) => {
      setSelectedAvatar(avatar);
      setSelectedLocale(locale);
      setSelectedGrammaticalGender(grammaticalGender);
      setPhase({ type: 'interview' });

      joinConference({
        avatar: avatar.id,
        allowAgentInterruptions: avatarEnabled ? true : undefined,
        locale,
        grammaticalGender,
      });
      setPollingInterval(POLLING_INTERVAL);
    },
    [joinConference, avatarEnabled]
  );

  const handleStartInterviewFromCompletion = useCallback(() => {
    if (!selectedAvatar || !selectedLocale) return;

    analyticsClient.trackEvent({
      name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_DEMO_START_REAL_INTERVIEW,
    });

    setPhase({ type: 'interview' });

    joinConference({
      avatar: selectedAvatar.id,
      allowAgentInterruptions: avatarEnabled ? true : undefined,
      locale: selectedLocale,
      grammaticalGender: selectedGrammaticalGender,
    });
    setPollingInterval(POLLING_INTERVAL);
  }, [
    selectedAvatar,
    selectedLocale,
    selectedGrammaticalGender,
    joinConference,
    avatarEnabled,
  ]);

  const handleGoAgain = useCallback(async () => {
    if (!selectedAvatar || !selectedLocale) return;

    analyticsClient.trackEvent({
      name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_DEMO_BACK_TO_CONFIG,
    });

    setPhase({ type: 'practice' });

    await joinDemoConference({
      avatar: selectedAvatar.id,
      allowAgentInterruptions: true,
      locale: selectedLocale,
      grammaticalGender: selectedGrammaticalGender,
    });
  }, [
    selectedAvatar,
    selectedLocale,
    selectedGrammaticalGender,
    joinDemoConference,
  ]);

  const handleBackToChooseInterviewer = useCallback(() => {
    const isAvatarFlow = avatarEnabled ?? false;
    const chooseStepIndex = isAvatarFlow ? 2 : 1;
    setPhase({ type: 'wizard', initialStepIndex: chooseStepIndex });
  }, [avatarEnabled]);

  const handleDemoEndCall = useCallback(() => {
    analyticsClient.trackEvent({
      name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_DEMO_TEST_CALL_END_BY_USER,
    });
    leaveDemoConference();
    setPhase({ type: 'practice-complete' });
  }, [leaveDemoConference]);

  async function handleEndInterview() {
    setPollingInterval(0);
    await leaveConference();
    setPhase({ type: 'thank-you' });
  }

  // Loading state
  if (avatarEnabled === null) {
    return (
      <Layout companyLogoUrl={companyLogoUrl} backUrl={exitUrl}>
        <Loader variant="bubbles" />
      </Layout>
    );
  }

  // Already completed
  if (
    conferenceStatus ===
    AiInterviewAssessmentStageActionCandidateStatus.Completed
  ) {
    return <ThankYou candidateId={aiInterviewCandidateId} exitUrl={exitUrl} />;
  }

  // Consent first: nothing of the interview flow renders until it is given.
  if (isConsentPending && consent) {
    return (
      <Layout companyLogoUrl={companyLogoUrl} backUrl={exitUrl}>
        {roleLoading ? (
          // The lead names the company: wait for it rather than flash a blank.
          <Loader variant="bubbles" />
        ) : (
          <ConsentGate
            aiInterviewCandidateId={aiInterviewCandidateId}
            status={consent.status}
            consentText={consent.consentText}
            companyName={companyName}
            exitUrl={exitUrl}
            onConsented={() => void refetchConference()}
            // A started interview can no longer be declined (server rule);
            // the gate still re-asks when the text changed.
            canDecline={
              conferenceStatus !==
              AiInterviewAssessmentStageActionCandidateStatus.InProgress
            }
          />
        )}
      </Layout>
    );
  }

  // Phase rendering
  function renderPhase() {
    switch (phase.type) {
      case 'wizard':
        return (
          <ConfigWizard
            estimatedDurationMinutes={estimatedDurationMinutes}
            timeModifierPercentage={timeModifierPercentage}
            isAvatarEnabled={avatarEnabled ?? false}
            remainingDemos={effectiveRemainingDemos}
            selectedLocales={selectedLocales}
            onStartPractice={handleStartPractice}
            onStartInterview={handleStartInterview}
            initialStepIndex={phase.initialStepIndex}
          />
        );

      case 'practice':
        return demoRoom ? (
          <>
            <DemoTestCallBanner
              formattedTime={formattedTime}
              conferenceId={conferenceId}
            />
            <ConferenceLobby
              room={demoRoom}
              isConnected={isDemoConnected}
              error={demoError}
              useAvatar={
                selectedAvatar?.id !== Avatar.Spot && selectedAvatar !== null
              }
              onEndInterview={handleDemoEndCall}
              endCallConfig={{
                title: t('aiInterview.demoConfig.endCallTitle'),
                message: t('aiInterview.demoConfig.endCallMessage'),
                confirmLabel: t('aiInterview.demoConfig.endCallConfirm'),
              }}
            />
          </>
        ) : (
          <Loader variant="bubbles" />
        );

      case 'practice-complete':
        return (
          <PracticeCompletion
            avatarName={selectedAvatar?.name ?? 'Spot'}
            remainingDemos={effectiveRemainingDemos}
            shouldContactUs={shouldContactUs}
            onStartInterview={handleStartInterviewFromCompletion}
            onGoAgain={handleGoAgain}
            onBackToChooseInterviewer={handleBackToChooseInterviewer}
          />
        );

      case 'interview':
        return (
          <ConferenceLobby
            room={room}
            isConnected={isConnected}
            error={error}
            useAvatar={
              selectedAvatar?.id !== Avatar.Spot && selectedAvatar !== null
            }
            onEndInterview={handleEndInterview}
          />
        );

      case 'thank-you':
        return (
          <ThankYou candidateId={aiInterviewCandidateId} exitUrl={exitUrl} />
        );
    }
  }

  return (
    <Layout companyLogoUrl={companyLogoUrl} backUrl={exitUrl}>
      {renderPhase()}
    </Layout>
  );
}
